import { useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"
import CircularProgress from "@mui/material/CircularProgress"
import Alert from "@mui/material/Alert"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { UrlApi } from "../../../../services/apirest.js"
import {
  formatearMontosEnTexto,
  getEstadoReciboConfig,
  parseEncabezadoRecibo,
} from "../utils/formatters.js"

/**
 * Muestra cómo se pagó una factura: los recibos imputados y el detalle de
 * cada medio de pago. Si la factura no tiene recibos imputados (o solo tiene
 * imputaciones anuladas) el endpoint devuelve [] y se muestra el cartel de
 * "todavía no fue pagada".
 *
 * Endpoint: GET /ctacte/recibos/facturas/descripcion/pagos/:idFactura
 * Respuesta: [{ id_recibo, descripcion: string[] }]
 *  - Puede venir más de un recibo (pago parcial / en cuotas): se itera y se
 *    renderiza un bloque por recibo.
 *  - descripcion son líneas ya armadas en texto: se renderizan tal cual, una
 *    por <li>. La cantidad de líneas varía (tarjeta / banco / efectivo /
 *    retenciones aparecen solo si corresponde). La línea "Recibo N°..." siempre
 *    está y trae el estado embebido ("... - Estado: <código>"): se extrae para
 *    mostrarlo como Chip (357 aprobado, 358 pendiente, 359 rechazado).
 */
function RecibosImputadosFactura({ idFactura }) {
  const [recibos, setRecibos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (idFactura === null || idFactura === undefined) {
      setRecibos([])
      setLoading(false)
      setError(false)
      return
    }

    let cancelado = false
    setLoading(true)
    setError(false)

    fetch(`${UrlApi}/ctacte/recibos/facturas/descripcion/pagos/${idFactura}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (cancelado) return
        setRecibos(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelado) setError(true)
      })
      .finally(() => {
        if (!cancelado) setLoading(false)
      })

    return () => {
      cancelado = true
    }
  }, [idFactura])

  if (loading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Buscando recibos imputados…
        </Typography>
      </Stack>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
        No se pudieron obtener los recibos de esta factura. Reintentá más tarde.
      </Alert>
    )
  }

  if (!recibos.length) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <InfoOutlinedIcon fontSize="small" color="disabled" />
        <Typography variant="body2" color="text.secondary">
          Esta factura todavía no fue pagada con ningún recibo.
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {recibos.map((recibo, index) => {
        const lineas = Array.isArray(recibo.descripcion) ? recibo.descripcion : []
        const [primeraLinea, ...restoLineas] = lineas
        const { encabezado, estado } = parseEncabezadoRecibo(primeraLinea)
        const codigoEstado = recibo.estado ?? estado
        const estadoConfig =
          codigoEstado !== null && codigoEstado !== undefined
            ? getEstadoReciboConfig(codigoEstado)
            : null

        return (
          <Box
            key={recibo.id_recibo ?? index}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ mb: restoLineas.length ? 1 : 0 }}
            >
              <ReceiptLongIcon fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatearMontosEnTexto(encabezado) ||
                  `Recibo N° ${recibo.id_recibo ?? "-"}`}
              </Typography>
              {estadoConfig && (
                <Chip size="small" label={estadoConfig.label} color={estadoConfig.color} />
              )}
            </Stack>

            {restoLineas.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 3, "& li": { mb: 0.25 } }}>
                {restoLineas.map((linea, i) => (
                  <Typography
                    key={i}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                  >
                    {formatearMontosEnTexto(linea)}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )
      })}
    </Stack>
  )
}

export default RecibosImputadosFactura
